import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CalendarDays, Save, Trash2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DaysInHand = () => {
  const [dih, setDih] = useState(null);
  const [editing, setEditing] = useState(false);
  const [days, setDays] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDih();
  }, []);

  const fetchDih = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/days-in-hand`);
      setDih(res.data.days_in_hand);
      if (res.data.days_in_hand) {
        setDays(res.data.days_in_hand.days?.toString() || '');
        setEffectiveDate(res.data.days_in_hand.effective_date || '');
      }
    } catch (err) {
      // ignore
    }
  };

  const handleSave = async () => {
    if (!days || !effectiveDate) {
      toast.error('Please enter both days and effective date');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/days-in-hand`, {
        days_in_hand: parseFloat(days),
        effective_date: effectiveDate
      });
      toast.success('Days in hand saved');
      setEditing(false);
      fetchDih();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/settings/days-in-hand`);
      toast.success('Days in hand cleared');
      setDih(null);
      setDays('');
      setEffectiveDate('');
      setEditing(false);
    } catch (err) {
      toast.error('Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="days-in-hand-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <CalendarDays className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-white font-medium text-sm">Starting Days in Hand</p>
              <p className="text-slate-400 text-xs mt-0.5">
                Set your pre-existing qualifying days from before you started using the app. This avoids needing to enter all historical trips.
              </p>
            </div>
          </div>
          {!editing && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs h-7 flex-shrink-0"
              data-testid="edit-dih-btn"
            >
              {dih ? 'Edit' : 'Set'}
            </Button>
          )}
        </div>

        {dih && !editing && (
          <div className="mt-3 ml-8 p-3 bg-amber-500/5 rounded-lg border border-amber-500/20">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-amber-400 text-lg font-bold">{dih.days} days</p>
                <p className="text-slate-500 text-[10px]">
                  as of {new Date(dih.effective_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <div className="flex items-start gap-1.5 ml-auto">
                <Info className="h-3.5 w-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-slate-500 max-w-[200px]">
                  This is added to your calculated days for display purposes. Your HS205 worksheet uses trip data only.
                </p>
              </div>
            </div>
          </div>
        )}

        {editing && (
          <div className="mt-3 ml-8 p-3 bg-slate-700/50 rounded-lg border border-slate-600 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Days in Hand</Label>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  step="0.5"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white h-9 mt-1"
                  placeholder="e.g. 120"
                  data-testid="dih-days-input"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Effective Date</Label>
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white h-9 mt-1"
                  data-testid="dih-date-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving}
                className="bg-amber-600 hover:bg-amber-700 h-7 text-xs" data-testid="dih-save-btn">
                <Save className="h-3 w-3 mr-1" />Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}
                className="text-slate-400 hover:text-white h-7 text-xs">
                Cancel
              </Button>
              {dih && (
                <Button size="sm" variant="ghost" onClick={handleClear} disabled={saving}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 text-xs ml-auto" data-testid="dih-clear-btn">
                  <Trash2 className="h-3 w-3 mr-1" />Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DaysInHand;
